import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Beaker, BookOpen } from "lucide-react";

const BG_URL = "https://static.prod-images.emergentagent.com/jobs/8c5fb318-3aa6-4bbe-9403-aec034306777/images/c9b5f7cc7cca6f273cba3fdb38c6ca13e2e2170062aeb2f8173937444582c1ae.png";

export default function AuthPage() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [profName, setProfName] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, profName || "Chen");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen w-full flex" data-testid="auth-page">
      {/* Left visual: lab desk image */}
      <div className="hidden lg:block lg:w-1/2 relative" style={{
        backgroundImage: `url(${BG_URL})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}>
        <div className="absolute inset-0" style={{
          background: "linear-gradient(180deg, rgba(244,239,230,0.0) 0%, rgba(244,239,230,0.0) 60%, rgba(244,239,230,0.9) 100%)"
        }} />
        <div className="absolute top-8 left-8">
          <div className="stamp" data-testid="brand-stamp">CARNET DU PROFESSEUR · KANTO N°001</div>
        </div>
        <div className="absolute bottom-10 left-8 right-8">
          <h1 className="font-serif-display text-5xl xl:text-6xl leading-[0.95] text-[#1A1A1A]" data-testid="hero-title">
            Le journal de bord<br/><em>d&apos;un Professeur Pokémon</em>
          </h1>
          <p className="mt-4 max-w-xl text-[#2B2B2B] text-sm">
            Recrutez des dresseurs, suivez leurs aventures à travers Kanto en temps réel,
            entraînez leurs Pokémon au labo et complétez votre Pokédex.
          </p>
        </div>
      </div>

      {/* Right form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12 relative">
        <div className="grain absolute inset-0" />
        <form onSubmit={submit} className="panel-strong p-8 w-full max-w-md relative z-10" data-testid="auth-form">
          <div className="flex items-center gap-3 mb-6">
            <Beaker size={22} className="text-[#8C2A2A]"/>
            <div>
              <div className="overline">Laboratoire · Kanto</div>
              <h2 className="font-serif-display text-3xl leading-none mt-1">
                {mode === "login" ? "Reprise du journal" : "Nouveau dossier"}
              </h2>
            </div>
          </div>

          {mode === "register" && (
            <div className="mb-4">
              <label className="overline block mb-1">Nom du professeur</label>
              <input
                data-testid="prof-name-input"
                className="ink-input"
                value={profName}
                onChange={(e) => setProfName(e.target.value)}
                placeholder="ex. Chen, Platane, Seko"
                required
              />
            </div>
          )}

          <div className="mb-4">
            <label className="overline block mb-1">Email</label>
            <input
              data-testid="email-input"
              type="email"
              className="ink-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="mb-4">
            <label className="overline block mb-1">Mot de passe</label>
            <input
              data-testid="password-input"
              type="password"
              className="ink-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={6}
              required
            />
          </div>

          {err && (
            <div data-testid="auth-error" className="text-[#8C2A2A] text-xs font-mono mb-3 border border-[#8C2A2A] p-2">
              {err}
            </div>
          )}

          <button
            data-testid="auth-submit-btn"
            type="submit"
            disabled={busy}
            className="btn-ink w-full justify-center py-3"
          >
            <BookOpen size={14}/>
            {busy ? "Traitement..." : mode === "login" ? "Ouvrir le journal" : "Démarrer la carrière"}
          </button>

          <div className="mt-6 text-center">
            <button
              type="button"
              data-testid="auth-toggle-btn"
              className="text-xs font-mono uppercase tracking-wider underline underline-offset-4 text-[#4A4A4A] hover:text-[#1A1A1A]"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); }}
            >
              {mode === "login" ? "Pas encore de labo ? Créer un compte" : "Déjà un compte ? Se connecter"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
